import React, { forwardRef, useEffect } from 'react';
import { feesAPI } from '../../services/feesService';

// const PaymentInvoice = forwardRef(({ student, payment, paymentDate ,originalPayableAmount,totalAmount,paymentHistoryId}, ref) => {
const PaymentInvoice = forwardRef(({ paymentHistoryId, onDataLoaded }, ref) => {
  const [receiptData, setReceiptData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const lastProcessedId = React.useRef(null);

  useEffect(() => {
    if (paymentHistoryId && paymentHistoryId !== lastProcessedId.current) {
      fetchReceiptData(paymentHistoryId);
    } else if (!paymentHistoryId) {
      lastProcessedId.current = null;
    }
  }, [paymentHistoryId]);

  const fetchReceiptData = async (id) => {
    try {
      setLoading(true);
      const res = await feesAPI.getReceiptData(id);
      if (res?.data?.success) {
        setReceiptData(res.data.data);
      }
    } catch (err) {
      console.error('fetchReceiptData error', err);
    } finally {
      setLoading(false);
      if (onDataLoaded && lastProcessedId.current !== id) {
        lastProcessedId.current = id;
        // Small delay to ensure state is updated and component re-rendered
        setTimeout(() => {
          onDataLoaded();
        }, 500);
      }
    }
  };

  if (loading || !receiptData) {
    return (
      <div className="flex justify-center items-center h-48">
        <p className="text-gray-500 italic">Preparing receipt...</p>
      </div>
    );
  }

  // Defensive data mapping
  const studentInfo = {
    firstName: receiptData.firstName || receiptData.student?.firstName || 'N/A',
    lastName: receiptData.lastName || receiptData.student?.lastName || '',
    admissionNumber: receiptData.admissionNumber || receiptData.student?.admissionNumber || 'N/A',
    className: receiptData.className || receiptData.student?.className || receiptData.class?.name || 'N/A',
    sectionName: receiptData.sectionName || receiptData.student?.sectionName || receiptData.section?.name || 'N/A',
    totalFeeAmount: receiptData.totalFeeAmount || 0,
    currentPaidAmount: receiptData.currentPaidAmount || receiptData.paidAmount || 0,
    totalPaidAmount: receiptData.totalPaidAmount || 0,
    remainingAmount: receiptData.remainingAmount ?? 0,
    feeType: receiptData.feeType || 'Fee',
    paymentMode: receiptData.paymentMode || 'N/A',
    paidAt: receiptData.paidAt || new Date()
  };

  return (
    <div ref={ref} className="p-8 bg-white text-gray-900 font-sans printable-invoice" style={{ width: '150mm', minHeight: '297mm', margin: 'auto' }}>
      {/* Header */}
      <div className="flex justify-between items-start border-b-2 border-blue-600 pb-6 mb-8">
        <div>
          <img src="/images/QuantaNovaLogo.jpeg" alt="QUANTANOVA Logo" className="h-16 w-auto mb-1" />
          <p className="text-xs text-gray-500">Plot No:26&27A,Rajiv Gruhakalpa Road</p>
          <p className="text-xs text-gray-500">Pragathi Nagar,Hyderabad</p>
        </div>
        <div className="text-right">
          <h2 className="text-2xl font-bold text-gray-800 uppercase mb-1">Fee Receipt</h2>
          <p className="text-sm font-semibold text-gray-600">Receipt No: <span className="text-blue-600">RCPT-{paymentHistoryId}</span></p>
          <p className="text-sm text-gray-600 font-medium">Date: {new Date(studentInfo.paidAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        </div>
      </div>

      {/* Student & Payment Summary */}
      <div className="grid grid-cols-2 gap-8 mb-10">
        <div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 border-b border-blue-100 pb-1">Student Details</h3>
          <div className="space-y-2">
            <p className="text-lg font-bold text-gray-900">{studentInfo.firstName} {studentInfo.lastName}</p>
            <p className="text-sm text-gray-600"><span className="font-semibold text-gray-700">Admission No:</span> {studentInfo.admissionNumber}</p>
            <p className="text-sm text-gray-600"><span className="font-semibold text-gray-700">Class/Section:</span> {studentInfo.className} / {studentInfo.sectionName}</p>
            <p className="text-sm text-gray-600"><span className="font-semibold text-gray-700">Total Fee Amount:</span> ₹{Number(studentInfo.totalFeeAmount).toLocaleString('en-IN')}</p>
          </div>
        </div>
        <div className="bg-blue-50 p-5 rounded-xl border border-blue-100 flex flex-col justify-center items-center">
          <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-1">Amount Paid</p>
          <p className="text-4xl font-black text-blue-700">₹{Number(studentInfo.currentPaidAmount).toLocaleString('en-IN')}</p>
          <p className="text-xs font-semibold text-blue-500 mt-1 uppercase">Fee Type: {studentInfo.feeType}</p>
          <p className="text-xs font-semibold text-blue-500 mt-1 uppercase">Payment Mode: {studentInfo.paymentMode}</p>

        </div>
      </div>

      {/* Payment Details Table */}
      <div className="mb-10">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-3 text-xs font-bold text-gray-600 uppercase">Description</th>
              <th className="p-3 text-xs font-bold text-gray-600 uppercase text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="p-3 text-sm text-gray-700">{studentInfo.feeType} Payment</td>
              <td className="p-3 text-sm text-gray-700 text-right font-bold">₹{Number(studentInfo.currentPaidAmount).toLocaleString('en-IN')}</td>
            </tr>
            <p className="text-xs font-semibold text-red-500 mt-2 uppercase border-t border-blue-200 pt-2 w-full">
              Total Paid Amount: ₹{Number(studentInfo.totalPaidAmount).toLocaleString('en-IN')}
            </p>
            <p className="text-xs font-semibold text-red-500 mt-2 uppercase border-t border-blue-200 pt-2 w-full">
              Remaining Amount To pay: ₹{Number(studentInfo.remainingAmount).toLocaleString('en-IN')}
            </p>
          </tbody>
        </table>
      </div>

      {/* Signature Section */}
      <div className="flex justify-between items-end mt-auto pt-16">
        <div className="text-center">
          <div className="w-48 border-b-2 border-gray-300 mb-2"></div>
          <p className="text-xs font-bold text-gray-600 uppercase">Guardian Signature</p>
        </div>
        <div className="text-center">
          <div className="w-48 border-b-2 border-blue-600 mb-2"></div>
          <p className="text-xs font-bold text-blue-600 uppercase tracking-widest">Authorized Signatory</p>
          <p className="text-[10px] text-gray-400 mt-1 italic">Generated via SMS Portal</p>
        </div>
      </div>

      {/* Footer Note */}
      <div className="mt-16 pt-8 border-t border-gray-200 text-center">
        <p className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-medium mb-1">Important Note</p>
        <p className="text-[10px] text-gray-400 italic">This is a computer-generated receipt and does not require a physical stamp. Please retain this for your academic records. Fees once paid are non-refundable.</p>
      </div>

      <style>{`
        @media print {
          /* Hide everything by default */
          body * {
            visibility: hidden !important;
          }
          /* Show only the printable content and its children */
          .printable-content, .printable-content * {
            visibility: visible !important;
          }
          /* Position the printable content at the top left */
          .printable-content {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Hide other elements to prevent extra pages */
          .no-print {
            display: none !important;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
});

export default PaymentInvoice;
